import React, { useEffect, useMemo, useRef } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { differenceInDays } from "date-fns";
import { Controller } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import useFormWithYup from "@/hooks/useFormWithYup";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { muiError, muiField } from "@/utils/hookForm";
import { YupSchema } from "@/utils/type";

export interface ProgramDetailsFormProps {
  defaultValues?: YupSchema<typeof validationSchema>;
  onSubmit?: (values: YupSchema<typeof validationSchema>) => void;
}

const useStyles = makeStyles()({
  textarea: {
    "& .MuiInputBase-multiline": {
      minHeight: 104,
    },
  },
});

const StyledCard = withStyles(Card, (theme) => ({
  root: {
    boxShadow: "none",
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(2, 1),
  },
}));

const StyledButton = withStyles(Button, (theme) => ({
  root: {
    padding: theme.spacing(2, 6),
  },
}));

const validationSchema = yup
  .object({
    name: yup.string().label("Plan name").required("${label} is required"),
    description: yup.string().label("Description").presence().notRequired(),
    startDate: yup
      .date()
      .label("Start date")
      .typeError("${label} is an invalid date")
      .presence()
      .required("${label} is required"),
    endDate: yup
      .date()
      .label("End date")
      .typeError("${label} is an invalid date")
      .presence()
      .required("${label} is required")
      .min(yup.ref("startDate"), "${label} must not be before start date"),
  })
  .defined();

const ProgramDetailsForm: React.FC<ProgramDetailsFormProps> = ({
  defaultValues,
  onSubmit,
}) => {
  const { classes } = useStyles();
  const { control, watch, reset, handleSubmit } = useFormWithYup({
    defaultValues: {
      name: "",
      startDate: null as Date | null,
      endDate: null as Date | null,
      description: "",
    },
    validationSchema,
  });

  const formRef = useRef<HTMLFormElement>(null);

  useKeyboardShortcut("Ctrl+S", () => {
    if (formRef.current) formRef.current.requestSubmit();
  });

  useEffect(() => {
    if (defaultValues) {
      reset({
        name: defaultValues.name,
        description: defaultValues.description,
        startDate: defaultValues.startDate,
        endDate: defaultValues.endDate,
      });
    }
  }, [defaultValues]);

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const durationDays = useMemo(() => {
    if (startDate == null || endDate == null) return null;

    return differenceInDays(endDate, startDate) + 1;
  }, [startDate, endDate]);

  return (
    <StyledCard elevation={0}>
      <form ref={formRef} onSubmit={handleSubmit(onSubmit ?? (() => {}))}>
        <CardContent>
          <Typography variant="h5" fontWeight={800}>
            Plan Details
          </Typography>
          <Box marginBottom={2} />
          <Grid container>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={8}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field, fieldState }) => (
                    <TextField
                      {...muiField(field)}
                      {...muiError(fieldState)}
                      placeholder="Plan name"
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  placeholder={durationDays?.toString() || "Duration"}
                  size="small"
                  type="number"
                  aria-readonly
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {durationDays && durationDays === 1 ? "day" : "days"}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <Grid item container xs={4}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="startDate"
                    render={({
                      field: { ref, name, onChange, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        onChange={(value) => onChange(value as Date)}
                        slotProps={{
                          textField: {
                            name,
                            fullWidth: true,
                            error: !!fieldState?.error,
                            helperText: fieldState?.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="endDate"
                    render={({
                      field: { ref, name, onChange, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        onChange={(value) => onChange(value as Date)}
                        slotProps={{
                          textField: {
                            name,
                            fullWidth: true,
                            error: !!fieldState?.error,
                            helperText: fieldState?.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid item xs={8}>
                <Controller
                  control={control}
                  name="description"
                  render={({ field, fieldState }) => (
                    <TextField
                      {...muiField(field)}
                      {...muiError(fieldState)}
                      className={classes.textarea}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Write description here..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Box marginTop={6} width="100%" textAlign="center">
            <StyledButton type="submit">Save</StyledButton>
          </Box>
        </CardContent>
      </form>
    </StyledCard>
  );
};

export default ProgramDetailsForm;
